import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcC = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule C: Simplifying Nonprofit Reporting"
          description="Simplify Form 990/990EZ Schedule C reporting. Navigate political campaign and lobbying activities compliance effortlessly with our guide."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-c/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule C</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your political and lobbying Activities with TaxZerone
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schc`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-c/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule C at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Purpose: Form 990 Schedule C</b> is used to report an
                        organization's{' '}
                        <b>
                          political campaign activities, lobbying activities,
                        </b>{' '}
                        and certain <b>Section 6033(e)</b> notice and reporting
                        requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File?</b> Organizations that engage in
                        <b> political campaign or lobbying activities</b> and
                        answer <b>"Yes"</b> to the applicable questions in{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990
                        </Link>
                        ,<b> Part IV (Checklist of Required Schedules)</b> must
                        complete and attach <b>Schedule C.</b>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>What Is Reported?</b> Schedule C reports{' '}
                        <b>
                          political campaign activities, lobbying expenditures,
                          grassroots and direct lobbying{' '}
                        </b>
                        , and <b>proxy tax reporting</b>, as applicable.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Activities Covered:</b> Schedule C generally applies
                        to
                        <b> Section 501(c) organizations</b> and certain{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-1120-pol/#difference"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Section 527
                        </Link>{' '}
                        political organizations engaged in reportable political
                        or lobbying activities.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule C?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule C?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schc" sx={{ color: '#0202ff' }}>
                          Schedule C Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule C filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Schedule C, </b>
                  <i>Political Campaign and Lobbying Activities</i> is a
                  supplementary schedule that accompanies the standard{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>
                  /{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>{' '}
                  and is filed annually by Section 501(c) organizations, and
                  Section 527 organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  It empowers organizations to provide comprehensive disclosures
                  regarding their involvement in political campaigns or lobbying
                  activities.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Non-profit organizations that file Form 990 or Form 990-EZ are
                  generally required to complete Schedule C if they have engaged
                  in any political campaign activities or lobbying activities
                  during the tax year. This includes activities such as making
                  contributions to candidates, engaging in grassroots lobbying,
                  or conducting direct lobbying.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If an organization has an ownership interest in a joint
                  venture that conducts political campaign activities or
                  lobbying activities, the organization must report its share of
                  such activity occurring in its tax year on Schedule C.
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>
                      Embark on a Smooth Schedule C Filing Journey with
                      TaxZerone
                    </b>
                    <br />
                    Complete your Schedule C filing requirements with ease!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schc`}
                  >
                    Stay Tax-Exempt With TaxZerone
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schc"
                >
                  Schedule C Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-organ-img.webp" />
                  <source srcSet="/assets/images/f990-shc-organ-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-organ-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C organization type information"
                    width={760}
                    height={294}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section 501(c) organizations and Section 527 organizations
                  that{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990
                  </Link>
                  /
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>{' '}
                  must complete and attach schedule c tax form along with their
                  organization&apos;s information{' '}
                  {/* <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>{' '} */}
                  return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule C filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I A -Complete if the organization is exempt under section
                  501(c) or is a section 527 organization
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part1-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part I-A - Complete if the organization is exempt under section 501(c) or is a section 527 organization."
                    width={760}
                    height={84}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe the political activities your organization
                  conducted during the year in Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount your organization spent on those
                  activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If volunteers helped with these activities, provide an
                  estimate of the total hours they contributed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I B-Complete if the organization is exempt under section
                  501(c)(3)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part1b-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part1b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part1b-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part I-B. Complete if the organization is exempt under section 501(c)(3) Line1 to Line 4"
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of attachment 4955 tax owed by the
                  organization. If none, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of attachment 4955 tax owed by any
                  organization managers. If none, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if you reported this tax on{' '}
                  <span style={{ fontWeight: 600 }}> Form 4720</span> for the
                  same tax year. Otherwise, select “No.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In Part IV, briefly explain the actions taken to correct the
                  issue. This may include recovering the political expenditure
                  (if possible) and putting safeguards in place to prevent it
                  from happening again.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I C-Complete if the organization is exempt under section
                  501(c), except section 501(c)(3)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part1c-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part1c-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part1c-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part C – Complete if the organization is exempt under section 501(c), except section 501(c)(3)"
                    width={760}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section applies if your organization engaged in political
                  activities described under section 527.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of your organization’s own funds used for
                  Section 527 political activities during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount your organization transferred to other
                  organizations (including your own separate segregated fund)
                  for Section 527 political activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 1 and 2. Enter the total here and on Form 1120-POL,
                  Line 17b.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if your organization reported taxable political
                  expenditures on Form 1120-POL for this year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part1c-line5-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part1c-line5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part1c-line5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part C – Complete if the organization is exempt under section 501(c), except section 501(c)(3) Line 5"
                    width={760}
                    height={330}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List each Section 527 political organization your organization
                  made payments to during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Columns (a), (b), and (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    name, address, and EIN.{' '}
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Columns (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the amount paid using your organization’s own funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Columns (e)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Report any political contributions you collected and
                    immediately forwarded to a separate political organization,
                    such as a segregated fund.
                  </li>
                  <li>
                    If there isn’t enough space, include additional information
                    in Part IV.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II A-Complete if the organization is exempt under section
                  501(c)(3) and filed Form 5768
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part2a-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part2a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part2a-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part II-A. Complete if the organization is exempt under section 501(c)(3) and filed Form 5768 (election under section 501(h))."
                    width={760}
                    height={80}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Item A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization is part of an affiliated
                  group. If selected, list each group member in Part IV,
                  including their name, address, EIN, total expenses, and share
                  of any excess lobbying expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Item B
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box only if you selected Box A, and the “limited
                  control” rules apply to your organization within the
                  affiliated group.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part2a-line1-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part2a-line1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part2a-line1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part II-A. Complete if the organization is exempt under section 501(c)(3) and filed Form 5768 line 1"
                    width={760}
                    height={302}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section is only for{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  that have chosen to file Form 5768 to make the 501(h)
                  election. If your organization hasn’t filed this form, you can
                  skip this section.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The total amounts reported by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    organization filing the return.
                  </span>
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The combined totals for the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    affiliated group members.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount spent on grassroots lobbying (encouraging the
                  public to contact legislators about specific legislation).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount spent on direct lobbying (communicating
                  directly with legislators or government officials about
                  specific legislation).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 1a and 1b. This is your total lobbying expense for
                  the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all other expenses related to carrying out your exempt
                  purpose, excluding lobbying costs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 1c and 1d. This is your total exempt purpose
                  expenditure amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total lobbying limit allowed for your organization.
                  This is calculated based on your exempt purpose expenditures
                  (Line 1e).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the maximum amount your organization is allowed to spend
                  specifically on grassroots lobbying. This is the portion of
                  your overall lobbying limit that applies only to activities
                  aimed at encouraging the public to act on legislation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount by which your total lobbying expenses went
                  over the allowed limit, if applicable. If you did not exceed
                  the limit, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount by which your grassroots lobbying expenses
                  exceed the allowed grassroots limit. If you stay within the
                  limit, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if your organization reported Section 4911 tax on
                  Form 4720 for this year. Otherwise, select no.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part2a-line2-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part2a-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part2a-line2-img.png"
                    loading="lazy"
                    alt="IRS Form €¯990 ScheduForm 990 Schedule C Part II-A. Complete if the organization is exempt under section 501(c)(3) and filed Form 5768 line 2"
                    width={760}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 2 is used to check whether your organization stayed
                  within the allowed lobbying limits over a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    4-year averaging period.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 2022, 2023, 2024, and 2025, enter the lobbying limit
                  amount from Part II-A, Line 1f, for each year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line calculates the total lobbying ceiling amount for the
                  4-year period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 2022–2025, enter the total lobbying expenses from Part
                  II-A, Line 1c, for each year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 2022–2025, enter the grassroots lobbying limit from Part
                  II-A, Line 1g, for each year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line calculates the grassroots lobbying ceiling amount
                  for the 4-year period.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 2022–2025, enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    grassroots lobbying expenses
                  </span>{' '}
                  from Part II-A, Line 1a, for each year. Add each row across
                  and enter the{' '}
                  <span style={{ fontWeight: 600 }}>totals in Column (e).</span>
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II B-Complete if the organization is exempt under section
                  501(c)(3) and has NOT filed Form 5768
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part2b-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part2b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part2b-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part II-B. Complete if the organization is exempt under section 501(c)(3) and has NOT filed Form 5768 Line 1"
                    width={760}
                    height={285}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section is for 501(c)(3) organizations that have not
                  filed Form 5768 (the 501(h) election) or those that previously
                  made the election but later revoked it. If either of these
                  applies to your organization, you should complete this
                  section.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization worked to influence legislation at any
                  level whether local, state, national, or even foreign- you are
                  required to disclose those activities and the costs associated
                  with them.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” or “No” to show whether the activity happened.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount spent on that activity. Even if no money was
                  spent, answer “Yes” in Column (a) if the activity took place
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Think about the people who gave their time. If supporters
                  helped with a legislative campaign or reached out to officials
                  on your behalf, mark "Yes." Since they aren't paid, you
                  usually won't have a dollar amount to be listed here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line accounts for the hours your paid team spent on
                  advocacy. While you don't list their salaries as a standalone
                  number here, you should include their prorated compensation in
                  the specific expense categories below (Lines 1c–1i).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you run ads on TV, radio, or social media to sway public
                  opinion or legislative votes, all those production and
                  placement costs go right her
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Whether it’s a physical letter with a stamp or a massive email
                  blast to your members about a new bill, track those
                  communication costs in this section.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This covers everything from your monthly newsletter to a press
                  release or a dedicated page on your website that tackles
                  specific legislation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Sometimes, the best way to lobby is to support another
                  organization’s work. If you give money to another group
                  specifically for their legislative efforts, report those funds
                  here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the costs for the "hands-on" work meetings with
                  legislators, phone calls to their offices, or trips to the
                  state house to speak with staff.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Rallies, seminars, and speeches intended to push for
                  legislative change can be expensive. Record the costs for
                  venues, speakers, and materials here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Advocacy doesn’t always fit into a neat box. If you did
                  something not listed above, put the costs here. If it's a
                  large amount, use Part IV to give a bit more detail on what
                  you did.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Simply add up the amounts from 1c through 1i in Column (b).
                  This total gives a clear picture of your organization's
                  financial commitment to advocacy for the year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part2b-line2-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part2b-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part2b-line2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part II-B. Complete if the organization is exempt under section 501(c)(3) and has NOT filed Form 5768 Line 2"
                    width={760}
                    height={65}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this section to report whether your organization’s
                  lobbying activities affected its 501(c)(3) tax-exempt status
                  and to disclose any related excise taxes, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the lobbying activities reported in Line 1
                  became substantial enough that the organization no longer
                  qualified as a{' '}
                  <Link
                    href="https://blog.taxzerone.com/posts/what-is-501c3-a-clear-guide-to-nonprofit-tax-exempt-status/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) tax-exempt organization
                  </Link>
                  . If the activities did not impact your exempt status, select
                  “No.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the answer to Line 2a is “Yes,” enter the amount of tax
                  charged to the organization under Section 4912, unless the tax
                  was later removed or reduced (abated). If no tax is applied,
                  enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any Section 4912 tax assessed against the organization’s
                  managers, if applicable. If no manager-level tax was imposed
                  or the amount was abated, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the organization reported and paid the Section
                  4912 tax by filing Form 4720 for the year. Otherwise, select
                  “No.”
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III A-Complete if the organization is exempt under
                  section 501(c)(4), section 501(c)(5), or section 501(c)(6)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part3a-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part3a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part3a-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part III showing information for 501(c)(4), 501(c)(5), or 501(c)(6) exempt organizations"
                    width={760}
                    height={96}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete this section only if your organization is exempt
                  under Internal Revenue Service Section 501(c)(4), 501(c)(5),
                  or 501(c)(6).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    90% or more of the membership dues received were not
                    tax-deductible for members.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Select “Yes” if the organization qualifies for the small
                  in-house lobbying exception. To qualify:
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1.2 } }}>
                  <li>
                    The organization did{' '}
                    <span style={{ fontWeight: 600 }}>not</span> make political
                    campaign or foreign lobbying expenditures during the year;
                    and
                  </li>
                  <li>
                    Its lobbying activity was limited only to{' '}
                    <span style={{ fontWeight: 600 }}>
                      in-house direct lobbying,
                    </span>{' '}
                    totaling{' '}
                    <span style={{ fontWeight: 600 }}>$2,000 or less,</span>{' '}
                    excluding overhead costs.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization had other lobbying or political expenses
                  during the year, even if in-house lobbying was $2,000 or less,
                  select “No.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the organization previously agreed to carry
                  forward lobbying or political campaign expenses from a prior
                  tax year and include them in the current year’s reasonable
                  estimate.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III B-Complete if the organization is exempt under
                  section 501(c)(4), section 501(c)(5), or section 501(c)(6)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part3b-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part3b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part3b-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C, Part III-B, completed by 501(c)(4), (c)(5), or (c)(6) organizations meeting specified Part III-A conditions."
                    width={760}
                    height={221}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part III-B is primarily for organizations exempt under
                  sections 501(c)(4), 501(c)(5), or 501(c)(6) that engage in
                  lobbying or political advocacy. The purpose of this section is
                  to show the IRS how membership dues were applied.
                  Specifically, it tracks whether any portion of those dues
                  funded lobbying efforts, as these amounts are generally not
                  deductible as business expenses for the members.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations filling out this section must choose one of two
                  paths: Notify members clearly about the percentage of their
                  dues used for these activities. Pay a proxy tax on those
                  expenditures instead of providing notice. Pay a proxy tax on
                  those expenditures instead of providing notice.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  On this line, enter the total sum of dues and related payments
                  received during the tax year. This total should account for:
                </Typography>
                <Box component="ul" sx={{ m: 2, '& li': { mb: 1.2 } }}>
                  <li>Standard membership dues.</li>
                  <li>Voluntary contributions from members. </li>
                  <li>
                    Assessments gathered to cover general operating costs.{' '}
                  </li>
                  <li>
                    Specialized fees collected specifically for political or
                    lobbying campaigns.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Essentially, this figure represents the total financial
                  support provided by members to keep the organization running
                  or to fund specific advocacy.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section identifies the money spent on activities that do
                  not qualify for tax deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report any costs incurred this year for the following:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Attempts to influence legislation.</li>
                  <li>Efforts to support or oppose political candidates. </li>
                  <li>
                    Campaigns intended to sway public opinion on elections or
                    specific laws.{' '}
                  </li>
                  <li>
                    Direct outreach to executive branch officials aimed at
                    influencing their decisions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do not include political expenses already taxed under section
                  527(f) or internal "in-house" lobbying costs if they totaled
                  $2,000 or less for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any lobbying or political costs that were brought
                  forward from the previous year. This usually happens if your
                  lobbying costs were higher than the total dues collected last
                  year, or if a proxy tax waiver was granted with the agreement
                  to adjust these costs later.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine{' '}
                  <span style={{ fontWeight: 600 }}> Lines 2a and 2b.</span>{' '}
                  This final sum represents your total nondeductible lobbying
                  and political spending for the current reporting period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization sent dues notices to members explaining
                  that part of their dues was nondeductible because of lobbying
                  or political activities, enter that total amount here.{' '}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Total membership dues = $100,000 </li>
                  <li>Members notified that 25% is nondeductible </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Line 3 amount = $25,000.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This shows the IRS that members were properly informed.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Only fill this out if you sent notices to members, but your
                  total lobbying costs (Line 2c) ended up being higher than the
                  amount you disclosed (Line 3). In this case, you can agree to
                  carry the excess over to next year’s estimate. If you did not
                  send notices, enter zero. Remember, any amount listed here
                  must be included as an expense on next year’s report.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line identifies the portion of lobbying-related dues that
                  members were not properly notified about. If notices were
                  missed or late, the organization might be liable for a tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The calculation depends on your specific financial
                  situation:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.51, fontWeight: 600 }}>
                  1. Dues exceed Lobbying Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Take your total lobbying expenses (Line 2c) and subtract both
                  the amount reported to members (Line 3) and any carryover
                  (Line 4).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.51, fontWeight: 600 }}>
                  2. Lobbying Expenses exceed Dues
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract the amounts from Lines 3 and 4 from your total dues
                  (Line 1).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any leftover expenses that aren't taxed now must be carried
                  forward to the following year. If there is a taxable balance,
                  it must be reported on{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-T
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-shc-part4-img.webp" />
                  <source srcSet="/assets/images/f990-shc-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-shc-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule C Part IV - Supplemental Information section for providing additional explanations and required narrative "
                    width={760}
                    height={265}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part IV gives your organization a place to add explanations or
                  extra details that don’t fit directly into the earlier parts
                  of Schedule C. Whenever the form asks for a written
                  description or more background information, this is where you
                  provide it so the filing can be clearly understood by the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Internal Revenue Service.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this section to support responses connected to:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 0, listStyleType: 'none', '& li': { mb: 1.1 } }}
                >
                  <li>Part I-A, Line 1 </li>
                  <li>Part I-B, Line 4 </li>
                  <li>Part I-C, Line 5 </li>
                  <li>Part II-A, Line 1 (list of affiliated organizations) </li>
                  <li>Part II-A, Lines 1 and 2 </li>
                  <li>Part II-B, Line 1 </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can also use Part IV anytime you feel an explanation will
                  help clarify what your organization reported elsewhere on
                  Schedule C.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule C filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Embrace the digital era of tax filing with TaxZerone, your
                  IRS-authorized e-file service provider. We&apos;re here to
                  revolutionize your tax filing experience, making it as simple
                  and stress-free as possible.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Our intuitive platform guides you seamlessly through every
                  step of the{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990{' '}
                  </Link>
                  /{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ filing
                  </Link>{' '}
                  process, making it a seamless experience.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your exempt organization&apos;s information
                  return with Schedule C attachment is transmitted to the IRS -
                  just 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Provide Organization Details -</b> Choose the tax year
                    for which you want to file a return, and provide your
                    organization&apos;s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Preview the Return -</b> Complete Schedule C and preview
                    the information provided in the return for accuracy before
                    transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Transmit to the IRS -</b> Transmit Schedule C along with
                    your Form 990/990-EZ return to the IRS and get the
                    acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In the unlikely event of an IRS rejection, we&apos;ll help you
                  identify and correct any errors in your information return,
                  ensuring a swift and cost-free resubmission.
                </Typography>
                {/* <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5 }}
                 
                >
                  <b>Note:</b> Don&apos;t include in Part VI the names of any
                  donors, grantors, or contributors because Part VI will be made
                  available for public inspection.
                </Alert> */}
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Streamline Your 990/990-EZ Filing with TaxZerone!
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 300, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schc`}
                      >
                        E-file Your Form 990/990-EZ
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sc.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule C (Form 990) – Political Campaign and
                          Lobbying Activities
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. When does Schedule C become a necessity?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule C is required to be filed when an organization has
                    engaged in political campaign activities or lobbying
                    activities during the tax year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is considered a &quot;political campaign
                  activity&quot; and a &quot;lobbying activity&quot;?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      <b>Political campaign activities</b> include any
                      activities that support or oppose a candidate for public
                      office or ballot measure.
                    </Typography>
                    <Typography>
                      <b>Lobbying activities</b> include any attempts to
                      influence legislation directly or indirectly.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What are the repercussions of failing to file Schedule C?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1">
                      Failure to file Schedule C or providing incomplete or
                      inaccurate information can result in significant
                      penalties, including late filing penalties and potential
                      loss of tax-exempt status.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcC;
